import { useState } from 'react';
import { toast } from 'react-toastify';
import EmptyPage from '../../../components/empty-page/emptyPage';
import CommonTable from '../../../components/reusable-ui-components/commonTable/CommonTable';
import Dialog from '../../../components/dialogModal/dialogModal';
import { supOrgCancelActGuBookings } from '../../../api/supportAdmin';
import errorHandle from '../../../utils/errorHandle';
import styles from './supBookingStay.module.css';

/*
 * Support > Bookings > Camp: stay bookings (StayBooked) of the organizer.
 * Cancel = full refund of what was paid (website) / just cancelled (manual),
 * and the rooms go back on sale — done on the server.
 */

const day = (val) => (val ? new Date(val).toLocaleDateString('en-IN') : 'N/A');

// "Dome Tent × 1, Dorm Bed × 2" (older bookings have one unit)
const roomsText = (row) => {
    const items = row?.items?.length ? row.items : [{ unitName: row?.unitName, rooms: row?.rooms }];
    return items.map((i) => `${i.unitName || 'Room'} × ${i.rooms || 1}`).join(', ');
};

const guestsText = (g = {}) =>
    [
        `${g.adults || 0} adult${g.adults === 1 ? '' : 's'}`,
        g.children ? `${g.children} child` : '',
        g.pets ? `${g.pets} pet` : ''
    ]
        .filter(Boolean)
        .join(', ');

const canCancel = (row) => !['Cancelled', 'Completed'].includes(row?.stayStatus);

// cancelBooking(id): support by default; the organizer side passes its own
const supportCancel = (bookingId) => supOrgCancelActGuBookings('camp', { bookingId });

export default function SupBookingStay({ bookingData, onChanged, cancelBooking = supportCancel }) {
    const [cancelRow, setCancelRow] = useState(null);
    const [busy, setBusy] = useState(false);

    const confirmCancel = async () => {
        setBusy(true);
        try {
            const res = await cancelBooking(cancelRow._id);
            const refund = res?.data?.refundAmount || 0;
            if (res?.data?.paymentStatus === 'Refund Failed') {
                toast.warn('Booking cancelled, but the refund could not be started. Please refund from Razorpay.');
            } else {
                toast.success(refund ? `Booking cancelled. Refund of ₹${refund} started.` : 'Booking cancelled.');
            }
            setCancelRow(null);
            onChanged?.();
        } catch (error) {
            errorHandle(error);
        } finally {
            setBusy(false);
        }
    };

    const columns = [
        {
            label: 'Stay',
            accessor: 'listingId',
            render: (val, row) => (
                <div>
                    <strong>{val?.title || row?.title || 'N/A'}</strong>
                    <div className={styles.sub}>{roomsText(row)}</div>
                </div>
            )
        },
        {
            label: 'Guest',
            accessor: 'userDetails',
            render: (val) => (
                <div>
                    {val?.[0]?.name || 'N/A'}
                    {val?.[0]?.phoneNumber && <div className={styles.sub}>{val[0].phoneNumber}</div>}
                </div>
            )
        },
        { label: 'Check-in', accessor: 'startDate', render: day },
        { label: 'Check-out', accessor: 'endDate', render: (val, row) => `${day(val)} (${row?.nights || 0}N)` },
        { label: 'Guests', accessor: 'guests', render: (val) => guestsText(val) },
        { label: 'Booked On', accessor: 'createdAt', render: day },
        {
            label: 'Payment',
            accessor: 'amountPaid',
            render: (val, row) => {
                const paid = Number(val?.paid) || 0;
                const total = Number(val?.total) || 0;
                return (
                    <div>
                        ₹{paid} paid
                        {val?.isReserved && (
                            <div className={styles.due}>
                                Reserved · ₹{Math.max(total - (Number(val?.reserve) || 0), 0)} at the stay
                            </div>
                        )}
                        {row?.paymentInfo?.refundAmount ? (
                            <div className={styles.sub}>
                                Refund ₹{row.paymentInfo.refundAmount} · {row.paymentInfo.status}
                            </div>
                        ) : null}
                    </div>
                );
            }
        },
        { label: 'Booked Via', accessor: 'bookedVia' },
        {
            label: 'Action',
            accessor: 'stayStatus',
            render: (val, row) =>
                canCancel(row) ? (
                    <div className={styles.action}>
                        <span>{val}</span>
                        <button
                            type='button'
                            className={styles.cancelBtn}
                            onClick={(e) => {
                                e.stopPropagation();
                                setCancelRow(row);
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                ) : (
                    <span className={val === 'Cancelled' ? styles.cancelled : ''}>{val}</span>
                )
        }
    ];

    const refund = cancelRow?.bookedVia === 'Manual' ? 0 : Number(cancelRow?.amountPaid?.paid) || 0;

    return (
        <div>
            {bookingData && bookingData.length !== 0 ? (
                <CommonTable data={bookingData} columns={columns} setRowClickId={() => {}} />
            ) : (
                <EmptyPage />
            )}

            {cancelRow && (
                <Dialog openModal={Boolean(cancelRow)} heading='' className={styles.cancelDialog} loading={busy}>
                    <div className={styles.cd}>
                        <div className={styles.cdHead}>
                            <span className={styles.cdIcon} aria-hidden='true'>
                                !
                            </span>
                            <div>
                                <h4 className={styles.cdTitle}>Cancel this stay?</h4>
                                <p className={styles.cdSub}>This can’t be undone.</p>
                            </div>
                        </div>

                        <div className={styles.cdBooking}>
                            <strong>{cancelRow?.listingId?.title || cancelRow?.title}</strong>
                            <span>
                                {cancelRow?.userDetails?.[0]?.name} · {day(cancelRow?.startDate)} –{' '}
                                {day(cancelRow?.endDate)}
                            </span>
                            <span className={styles.sub}>{roomsText(cancelRow)}</span>
                        </div>

                        <div className={styles.cdRefund}>
                            <span>{refund ? 'Refund to guest' : 'Refund'}</span>
                            <strong>₹{refund.toLocaleString('en-IN')}</strong>
                        </div>
                        <p className={styles.cdNote}>
                            {refund
                                ? 'Everything the guest paid goes back to their original payment method.'
                                : 'Manual booking — no online refund. Settle with the guest directly.'}{' '}
                            Rooms go back on sale and the guest gets an email &amp; WhatsApp.
                        </p>

                        <div className={styles.cdButtons}>
                            <button
                                type='button'
                                className={styles.ghostBtn}
                                onClick={() => setCancelRow(null)}
                                disabled={busy}
                            >
                                Keep booking
                            </button>
                            <button type='button' className={styles.dangerBtn} onClick={confirmCancel} disabled={busy}>
                                {refund ? 'Cancel & refund' : 'Cancel booking'}
                            </button>
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
